/**
 * Shared utility for capturing and retrieving UTM parameters.
 * Used by both gatsby-browser.js (for global capture) and
 * Typeform components (for reading with fallback).
 */

export const UTM_STORAGE_KEY = 'ff_utm_params';

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'fbp',
  'fbc',
];

/**
 * Captures UTM parameters from the current URL and stores them in sessionStorage.
 * Only updates storage if UTM params are found in the URL.
 *
 * @returns {Record<string, string> | null} The captured UTM data, or null if none found
 */
export function captureUtmParameters() {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  /** @type {Record<string, string>} */
  const utmData = {};
  let hasUtmParams = false;

  UTM_PARAMS.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmData[key] = value;
      hasUtmParams = true;
    }
  });

  // Only update sessionStorage if we found UTM params in the URL
  if (hasUtmParams) {
    try {
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    } catch (e) {
      // Silently fail if sessionStorage is unavailable
    }
    return utmData;
  }

  return null;
}

/**
 * Retrieves UTM parameters from sessionStorage, with fallback to URL.
 * First checks sessionStorage, then falls back to reading directly from
 * the URL if storage is empty (handles race conditions).
 *
 * @returns {Record<string, string>} UTM parameters object
 */
export function getUtmParameters() {
  if (typeof window === 'undefined') {
    return {};
  }

  // First, try to retrieve UTM params from sessionStorage
  try {
    const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Silently fail if sessionStorage is unavailable or invalid JSON
  }

  // Fallback: Read directly from URL and store for next time
  const captured = captureUtmParameters();
  return captured || {};
}
